import { Link } from "react-router-dom";
import { formatEuro } from "@/lib/helpers";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Table } from 'lucide-react';
import AprilPicto from '@/components/shared/april-picto';
import { PictoContrats } from '@/assets/icons-april-fr/pictos';
import APILink from "@/components/shared/api-link";
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { getReceiptsLastYear } from '@/hooks/api/use-payments';
import { getLastReceipt, useContractReceipts } from '@/hooks/api/use-receipts';
import { pushGoogleEvent } from '@/lib/analytics';
import { getContractFractionnementLabel, isActionAvailable } from '@/lib/contracts';
import { formatDate } from '@/lib/dates';
import { PDF } from '@/types/api/endpoints';
import type { Quittance } from '@/types/api/payment';

const getReceiptsDownload = () =>
    pushGoogleEvent({
        event: 'click',
        actionGoogle: 'clickButton',
        libelle: 'Je télécharge mon historique des quittances',
    });

const CotisationSelectedContract = () => {
    const contract = useSelectedContract();
    const id_contrat = contract?.id;
    // Relu à chaque montage, comme l'ancien `load`.
    const receiptsQuery = useContractReceipts(id_contrat);
    const allReceipts = receiptsQuery.data as Quittance[] | undefined;
    const receipts = getReceiptsLastYear(allReceipts);
    const show_tableau_prelevement = getContractFractionnementLabel(contract) === 'mensuel' && Boolean((getLastReceipt(allReceipts) as Quittance | undefined)?.has_tableau_prelevement);
    const canExportReceipts = id_contrat !== undefined && isActionAvailable(contract, 'telecharger_quittances');

    if (receiptsQuery.isPending) return <SmallSpinner />;

    return (
        <div className="space-y-4">
            {show_tableau_prelevement && (
                <div className="text-center">
                    <Button
                        asChild
                        variant="outline"
                        className="cursor-pointer whitespace-normal py-6"
                    >
                        <Link to={`/mes-paiements/mon-contrat/${id_contrat}/mes-prelevements`}>
                            <Table className="w-4 h-4 shrink-0" />
                            Accéder à mon tableau de prélèvement
                        </Link>
                    </Button>
                </div>
            )}

            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoContrats} size="md" />
                        Mes quittances sur les 12 derniers mois
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 text-center">
                    {receipts && receipts.length >= 1 ? (
                        <>
                            <div className="space-y-3">
                                {receipts.map((receipt, i) => (
                                    <div
                                        key={i}
                                        className="grid grid-cols-1 sm:grid-cols-3 items-center gap-1 sm:gap-4 p-4 text-sm bg-muted/50 rounded-lg transition-all duration-200 hover:bg-primary/10 hover:translate-x-1"
                                    >
                                        <p className="font-medium">
                                            Du {formatDate(receipt.dateDebut)} au{' '}
                                            {formatDate(receipt.dateFin)}
                                        </p>
                                        <p>
                                            Montant : <strong>{formatEuro(receipt.montant)}</strong>
                                        </p>
                                        <p>
                                            Motif : <strong>{receipt.type}</strong>
                                        </p>
                                    </div>
                                ))}
                            </div>
                            {canExportReceipts && (
                                <div className="text-center mt-6">
                                    <APILink
                                        href={PDF.receiptsSchedule(id_contrat)}
                                        onClick={() => getReceiptsDownload()}
                                        download
                                        className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md border cursor-pointer no-underline"
                                    >
                                        <Download className="w-4 h-4 shrink-0" />
                                        Télécharger mon historique des quittances
                                    </APILink>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Aucune quittance n'a été trouvée pour les 12 derniers mois.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default CotisationSelectedContract;
